import {Filter, type FilterProps} from '@github-ui/filter'
import {isNestedFilterProvider, ProviderSupportStatus} from '@github-ui/filter-core'
import {IssueTypeFilterProvider} from '@github-ui/issue-type-filter-provider'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useFilterProviders} from '@github-ui/shared-filter-providers/providers/use-filter-providers'
import {useOrgAwareProviders} from '@github-ui/shared-filter-providers/providers/use-org-aware-providers'
import {type ColorName, type ColorSet, useGetPresentationalColor} from '@github-ui/use-named-color'
import {memo, useCallback, useId, useImperativeHandle, useMemo, useRef} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import {useTableHierarchy} from '../../features/sub-issues/use-table-hierarchy'
import {assertNever} from '../../helpers/assert-never'
import {getInitialState} from '../../helpers/initial-state'
import {isFilterableColumn} from '../../models/column-capabilities'
import type {ColumnModel} from '../../models/column-model'
import {useAllColumns} from '../../state-providers/columns/use-all-columns'
import {Resources} from '../../strings'
import styles from './filter-bar.module.css'
import {
  BlockedByFilterProvider,
  BlockingFilterProvider,
  DateFilterProvider,
  FILTER_PRIORITIES,
  IterationFilterProvider,
  LastUpdatedFilterProvider,
  LinkedPullRequestsFilterProvider,
  MemexAssigneeFilterProvider,
  MemexIsFilterProvider,
  MemexLabelFilterProvider,
  MemexMilestoneFilterProvider,
  MemexParentIssueFilterProvider,
  MemexRepositoryFilterProvider,
  MemexReviewersFilterProvider,
  MemexStateFilterProvider,
  NumberFilterProvider,
  ReasonFilterProvider,
  ReviewsFilterProvider,
  SingleSelectFilterProvider,
  SubIssuesProgressFilterProvider,
  TextFilterProvider,
  TitleFilterProvider,
} from './filter-providers'
import {buildSubIssueNestedProvider} from './sub-issue-filter-provider'
import {useHandleFilterBarShortcut} from './use-handle-filter-bar-shortcut'

export type FilterBarProps = Pick<
  FilterProps,
  'filterValue' | 'onChange' | 'context' | 'onValidation' | 'onKeyDown' | 'onSubmit' | 'trailingVisual'
> & {
  /**
   * Optional ID for the Filter component.
   */
  id?: string
  /**
   * If provided, only these filter providers will be used.
   * This is useful for limiting filter support for a specific context like a workflow.
   */
  limitedProviderKeys?: Array<string>
  /**
   * Accessible label for the form element.
   */
  formLabel?: string
  /**
   * An optional ref for the input element.
   */
  inputRef?: React.RefObject<HTMLInputElement | null>
  /**
   * Enables OR/AND/parentheses syntax in the filter input.
   * Only enable this when the backend supports ConditionalIssueQuery parsing.
   * @default false
   */
  groupAndKeywordSupport?: boolean
}

type GetFilterProviderForColumnParams = {
  column: ColumnModel
  getPresentationalColor: (color: ColorName) => ColorSet
  userFilterParams: {showAtMe: boolean; currentUserLogin?: string; currentUserAvatarUrl?: string}
  projectScope?: {login?: string; projectNumber?: number} | undefined
  context?: FilterProps['context']
}

/**
 * Resolves column name collisions by preferring issue fields over project fields.
 * This handles the edge case where a user has a project field with a name, and then
 * an issue field gets renamed to the same name. Issue fields take precedence.
 */
export const resolveColumnNameCollisions = (columns: Array<ColumnModel>): Array<ColumnModel> => {
  const columnMap = new Map<string, ColumnModel>()
  for (const column of columns) {
    const existing = columnMap.get(column.name)
    if (!existing) {
      columnMap.set(column.name, column)
    } else if (column.isIssueField && !existing.isIssueField) {
      columnMap.set(column.name, column)
    }
  }
  return Array.from(columnMap.values())
}

type ColumnFilterProviderParams = Omit<GetFilterProviderForColumnParams, 'column'> & {
  allColumns: Array<ColumnModel>
}

/**
 * Creates filter providers for the given project columns.
 */
const createColumnFilterProviders = ({allColumns, ...rest}: ColumnFilterProviderParams) => {
  return resolveColumnNameCollisions(allColumns)
    .filter(column => isFilterableColumn(column.dataType))
    .map((column: ColumnModel) => getFilterProviderForColumn({...rest, column}))
}

/**
 * Hook to create filter providers for project columns
 */
const useColumnFilterProviders = ({
  allColumns,
  getPresentationalColor,
  userFilterParams,
  projectScope,
  context,
}: ColumnFilterProviderParams) => {
  return useMemo(
    () =>
      createColumnFilterProviders({
        allColumns,
        getPresentationalColor,
        userFilterParams,
        projectScope,
        context,
      }),
    [allColumns, getPresentationalColor, userFilterParams, projectScope, context],
  )
}

/**
 * These providers are defined specifically to try to reduce scope for the user
 * so that they can quickly jump to items they're probably trying to filter for,
 * rather than search. We make the assumption that they want to be looking within
 * items that are already imported, and so we filter down from all possible responses
 */
const getFilterProviderForColumn = ({
  column,
  getPresentationalColor,
  userFilterParams,
  projectScope,
  context,
}: GetFilterProviderForColumnParams) => {
  const {dataType} = column

  switch (dataType) {
    case MemexColumnDataType.Assignees:
      return new MemexAssigneeFilterProvider(column, userFilterParams, {
        filterTypes: {exclusive: true, hasValue: true},
      })
    case MemexColumnDataType.Closed:
    case MemexColumnDataType.Date:
      return new DateFilterProvider(column)
    case MemexColumnDataType.Created:
    case MemexColumnDataType.Updated:
      return new DateFilterProvider(column, {filterTypes: {valueless: false, hasValue: false}})
    case MemexColumnDataType.Iteration:
      return new IterationFilterProvider(column)
    case MemexColumnDataType.Labels: {
      const options = {
        filterTypes: {exclusive: true, hasValue: true},
      }
      // Suggests labels across all repositories in the project; no validation.
      return new MemexLabelFilterProvider(column, options)
    }
    case MemexColumnDataType.LinkedPullRequests:
      return new LinkedPullRequestsFilterProvider({
        filterTypes: {hasValue: true},
      })
    case MemexColumnDataType.Milestone: {
      const options = {
        priority: FILTER_PRIORITIES.milestone,
        filterTypes: {exclusive: true, hasValue: true},
      }
      // Suggests milestones across all repositories in the project; no validation.
      return new MemexMilestoneFilterProvider(column, options)
    }
    case MemexColumnDataType.IssueType: {
      // Suggests and validates issue types for a specific repository
      const repoScope = context ? context['repo'] : undefined
      // Fall back to suggesting issue types from the project's organization if we aren't suggesting them from a particular repo (and if we have the necessary context to do so)
      const fallbackProjectScope = repoScope ? undefined : projectScope
      return new IssueTypeFilterProvider(
        {priority: FILTER_PRIORITIES.type, filterTypes: {valueless: true, hasValue: true}},
        false,
        repoScope,
        fallbackProjectScope,
      )
    }
    case MemexColumnDataType.Number:
      return new NumberFilterProvider(column)
    case MemexColumnDataType.Repository:
      return new MemexRepositoryFilterProvider(column, {filterTypes: {exclusive: true, hasValue: true}})
    case MemexColumnDataType.Reviewers:
      return new MemexReviewersFilterProvider(userFilterParams, {
        filterTypes: {exclusive: true, hasValue: true},
      })
    case MemexColumnDataType.SingleSelect:
      return new SingleSelectFilterProvider(column, getPresentationalColor)
    case MemexColumnDataType.MultiSelect:
      // This branch is unreachable in practice because the upstream `isFilterableColumn`
      // guard filters multi-select columns out before they reach this switch.
      throw new Error('Multi-select filter provider is not implemented')
    case MemexColumnDataType.SubIssuesProgress:
      return new SubIssuesProgressFilterProvider(column, {filterTypes: {hasValue: true}})
    case MemexColumnDataType.Text:
      return new TextFilterProvider(column)
    case MemexColumnDataType.Title:
      return new TitleFilterProvider(column)
    case MemexColumnDataType.ParentIssue:
      return new MemexParentIssueFilterProvider(column, {filterTypes: {hasValue: true}})
    default: {
      return assertNever(dataType)
    }
  }
}

/** Universal providers that work both in and out of project context
 * For these, it doesn't matter if the user is trying to get quickly to items
 * that might be in their project, or if they're looking across GitHub.
 */
const createSharedProviders = () => {
  return [
    new LastUpdatedFilterProvider({
      support: {
        status: ProviderSupportStatus.Deprecated,
        message:
          'The "last-updated" filter is deprecated and will be removed in the future. Please use the new "updated" filter instead.',
      },
    }),
    new MemexIsFilterProvider(['issue', 'pr', 'open', 'closed', 'draft', 'merged', 'blocked', 'blocking'], {
      filterTypes: {valueless: false},
    }),
    new MemexStateFilterProvider('mixed', {
      priority: FILTER_PRIORITIES.state,
      filterTypes: {valueless: false},
    }),
    new ReasonFilterProvider(),
    new ReviewsFilterProvider(),
    new BlockedByFilterProvider({filterTypes: {hasValue: true}}),
    new BlockingFilterProvider({filterTypes: {hasValue: true}}),
  ]
}

export const FilterBar = memo(function FilterBar({
  id,
  filterValue,
  onChange,
  context,
  onValidation,
  formLabel,
  inputRef: givenInputRef,
  onKeyDown,
  limitedProviderKeys,
  trailingVisual,
  groupAndKeywordSupport = false,
}: FilterBarProps) {
  const generatedId = useId()
  const filterId = id ?? `filter-bar${generatedId}`

  const {allColumns} = useAllColumns()
  const {loggedInUser, projectOwner, projectData, isOrganization} = getInitialState()
  const {getPresentationalColor} = useGetPresentationalColor()
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isIssueFieldsGlobalSearchEnabled = useFeatureFlag('issue_fields_global_search')

  const sharedProviders = useMemo(() => createSharedProviders(), [])

  const projectScope = useMemo(
    () => ({login: projectOwner?.login, projectNumber: projectData?.number}),
    [projectOwner?.login, projectData?.number],
  )

  const userFilterParams = useMemo(
    () =>
      loggedInUser
        ? {
            showAtCopilot: true,
            showAtMe: true,
            currentUserLogin: loggedInUser.login,
            currentUserAvatarUrl: loggedInUser.avatarUrl,
          }
        : {showAtMe: false},
    [loggedInUser],
  )

  // Project-board-specific providers built from board columns
  const columnFilterProviders = useColumnFilterProviders({
    allColumns,
    getPresentationalColor,
    userFilterParams,
    projectScope,
    context,
  })

  /** GitHub-wide providers for bulk-add/repo-scoped contexts
   * These will search for every value for the user, and need to be provided specifically
   * because they might not have items they're looking for on a project board just yet.
   * This is particularly relevant to e.g. an import task in a new project with no items
   */
  const globalFilterProviders = useFilterProviders({
    repositoryScope: context?.repo,
    isOrgScope: isOrganization,
    isIssueFieldsEnabled: false,
    isIssueFieldsGlobalSearchEnabled,
    providerOverrides: {
      type: {
        legacy: false,
        projectScope: isOrganization ? projectScope : undefined,
      },
    },
  })

  const orgAwareProviders = useOrgAwareProviders({
    baseFilterProviders: globalFilterProviders,
    effectiveQuery: filterValue ?? '',
    isIssueFieldsGlobalSearchEnabled,
  })

  const {hierarchyEnabled} = useTableHierarchy()

  // Create new provider instances for the sub-issue nested provider so that
  // each class is properly instantiated (avoiding issues with private fields).
  const subIssueNestedProvider = useMemo(() => {
    const nestedSharedProviders = createSharedProviders()
    const nestedColumnProviders = createColumnFilterProviders({
      allColumns,
      getPresentationalColor,
      userFilterParams,
      projectScope,
      context,
    })
    return buildSubIssueNestedProvider([...nestedSharedProviders, ...nestedColumnProviders], hierarchyEnabled)
  }, [allColumns, getPresentationalColor, userFilterParams, projectScope, context, hierarchyEnabled])

  const providers = useMemo(() => {
    const isOutsideProjectContext = context?.searchType === 'global'
    const contextualProviders = isOutsideProjectContext ? orgAwareProviders : columnFilterProviders
    const allProviders = [...sharedProviders, ...contextualProviders]

    if (!isOutsideProjectContext) {
      allProviders.push(subIssueNestedProvider)
    }

    if (limitedProviderKeys) {
      return allProviders.filter(
        provider => !isNestedFilterProvider(provider) && limitedProviderKeys.includes(provider.key),
      )
    }
    return allProviders
  }, [orgAwareProviders, columnFilterProviders, context, limitedProviderKeys, subIssueNestedProvider, sharedProviders])

  const inputRef = useRef<HTMLInputElement>(null)
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(givenInputRef, () => inputRef.current)

  const onFilterBarShortcut = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  useHandleFilterBarShortcut(onFilterBarShortcut)

  return (
    <Filter
      id={filterId}
      data-testid={filterId}
      label={formLabel ?? 'Filter'}
      filterButtonVariant="compact"
      settings={{
        aliasMatching: true,
        groupAndKeywordSupport,
      }}
      variant="input"
      aria-label={Resources.filterByKeyboardOrByField}
      placeholder={Resources.filterByKeyboardOrByField}
      filterValue={filterValue}
      inputRef={inputRef}
      onChange={onChange}
      onValidation={onValidation}
      onKeyDown={onKeyDown}
      context={context}
      providers={providers}
      className={styles.Filter_0}
      trailingVisual={trailingVisual}
    />
  )
})
